import PubSub from "pubsub-js";
import { type Maybe, Nothing } from "purify-ts";
import { find, propEq } from "ramda";
import { createContext, useContext } from "react";
import { createStore, type StateCreator, type StoreApi } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { PubSubActions } from "@/global/pubsub-actions.ts";
import { SupportedExecutorsEnum, type TrackerParam } from "@/graphql.ts";
import { getApplicationConfig } from "@/modules/app/utils/get-configuration.ts";
import {
  createActivitySlice,
  initialActivitySliceState,
} from "@/modules/workflow/stores/activity/workflow-editor-store-activity-slice.ts";
import {
  createCopyPasteSlice,
  initialCopyPasteSliceState,
} from "@/modules/workflow/stores/copy-paste/workflow-editor-store-copy-paste-slice.ts";
import {
  createErrorSlice,
  initialErrorsSliceState,
} from "@/modules/workflow/stores/errors/workflow-editor-store-errors-slice.ts";
import {
  createExecutionSlice,
  initialExecutionSliceState,
} from "@/modules/workflow/stores/execution/workflow-editor-store-execution-slice.ts";
import {
  createLoadSaveSlice,
  initialLoadSaveSliceState,
} from "@/modules/workflow/stores/load-save/workflow-editor-store-load-save-slice.ts";
import {
  createReactFlowSlice,
  initialReactFlowSliceState,
} from "@/modules/workflow/stores/reactflow/workflow-editor-store-reactflow-slice.ts";
import {
  createUndoRedoSlice,
  initialUndoRedoSliceState,
} from "@/modules/workflow/stores/undo-redo/workflow-editor-store-undo-redo-slice.ts";
import type {
  WorkflowEditorState,
  WorkflowEditorStore,
} from "@/modules/workflow/stores/workflow-editor-store-types.ts";
import { getValueFromWorkflowParameter } from "@/modules/workflow/utils/activity.ts";
import { getDownstreamNodes, getUpstreamNodes } from "@/modules/workflow/utils/graph-utils.ts";
import {
  type ActivityParameter,
  type AllParametersTypes,
  type WorkflowActivityParameterFieldProps,
  type WorkflowParameter,
} from "@/modules/workflow/utils/types";
import { generateValidationSchema } from "@/modules/workflow/utils/validationSchema";

export const initialState: WorkflowEditorState = {
  parametersRecord: {},
  sampleTypeId: undefined,
};

const storeCreator: StateCreator<WorkflowEditorStore> = (set, get, store) => {
  PubSub.subscribe(PubSubActions.LOGOUT, () => {
    set(initialState);
  });

  return {
    ...createErrorSlice(set, get, store),
    ...createLoadSaveSlice(set, get, store),
    ...createActivitySlice(set, get, store),
    ...createExecutionSlice(set, get, store),
    ...createReactFlowSlice(set, get, store),
    ...createCopyPasteSlice(set, get, store),
    ...createUndoRedoSlice(set, get, store),
    ...initialState,
    setActivityParameterStaged(nodeId, parameterId, staged = false) {
      const currentParams = get().getParameters(nodeId);
      if (!currentParams) return;

      const parameterIndex = currentParams.findIndex((p) => p.parameterId === parameterId);
      if (parameterIndex === -1) return;

      const updatedParams = currentParams.map((param, index) =>
        index === parameterIndex ? { ...param, staged } : param,
      );
      set((state) => ({
        parametersRecord: {
          ...state.parametersRecord,
          [nodeId]: updatedParams, // new list required to trigger render of the staged switch
        },
      }));
    },
    setActivityParameterValue(nodeId, parameterId, values, skipParameterValidation) {
      const node = get().getNode(nodeId);
      const activity = node ? get().getActivity(node.data.activityName, node.data.version) : null;
      const activityParameterFromDefinition: ActivityParameter | undefined = find(
        (parameter) => parameter.id === parameterId,
        activity?.parameters ?? [],
      );
      const existingWorkflowParameter = get()
        .getParameters(nodeId)
        .find((p) => p.parameterId === parameterId);

      // Only require activityParameterFromDefinition to exist
      if (!activityParameterFromDefinition) return;

      const param: WorkflowParameter = {
        nodeId,
        parameterId,
        typeName: activityParameterFromDefinition.__typename,
        tracker: (activityParameterFromDefinition as TrackerParam).tracker ?? null,
        values: values == undefined ? Nothing : (values as Maybe<AllParametersTypes>),
        staged: existingWorkflowParameter?.staged ?? false, // Use default if no existing parameter
        schema: generateValidationSchema({
          nodeId,
          ...activityParameterFromDefinition,
        } as WorkflowActivityParameterFieldProps),
      } as WorkflowParameter;

      const currentParams = get().getParameters(nodeId);
      const existingIndex = currentParams.findIndex((p) => p.parameterId === parameterId);

      if (existingIndex >= 0) {
        // Update existing parameter
        const updatedParams = currentParams.map((p, index) => (index === existingIndex ? param : p));

        set({
          parametersRecord: {
            ...get().parametersRecord,
            [nodeId]: updatedParams,
          },
        });
      } else {
        // Append new parameter
        // FIXME: This should not be needed anymore but does not hurt neither
        set({
          parametersRecord: {
            ...get().parametersRecord,
            [nodeId]: [...currentParams, param],
          },
        });
      }

      if (!skipParameterValidation) {
        get().checkSingleParameter(nodeId, parameterId);
      }
    },
    async registerActivityParameters(nodeId, activityParameters) {
      const node = get().getNode(nodeId);

      if (!node) return;

      const activity = get().getActivity(node.data.activityName, node.data.version);

      // FIXME List/Notify all parameters that are now wrong because the activity changed. See below.

      if (activity?.parameters) {
        const parametersBuffer: WorkflowParameter[] = [];

        for (const parameter of activity.parameters) {
          // activity instance
          const workflowActivityParam = activityParameters
            ? find(propEq(parameter.id, "id"), activityParameters)
            : undefined;

          // activity definition
          const activityParameterFromDefinition: ActivityParameter | undefined = (activity.parameters ?? []).find(
            (param) => param.id === parameter.id,
          );

          const values = await getValueFromWorkflowParameter(parameter, workflowActivityParam, !activityParameters);

          const parameterToRegister: WorkflowParameter = {
            nodeId,
            parameterId: parameter.id,
            typeName: activityParameterFromDefinition?.__typename,
            tracker: (activityParameterFromDefinition as TrackerParam).tracker ?? null,
            values: values == undefined ? Nothing : (values as Maybe<AllParametersTypes>),
            staged: workflowActivityParam?.staged ?? false, // Use default if no existing parameter
            schema: generateValidationSchema({
              nodeId,
              ...activityParameterFromDefinition,
            } as WorkflowActivityParameterFieldProps),
          } as WorkflowParameter;

          parametersBuffer.push(parameterToRegister);
        }
        set({
          parametersRecord: {
            ...get().parametersRecord,
            [nodeId]: parametersBuffer,
          },
        });
      }
    },
    async setActivityComputing(nodeId, computing, force = false) {
      const node = get().getNode(nodeId);
      if (!node) return;
      const activity = get().getActivity(node.data.activityName, node.data.version);

      if (computing != undefined) {
        if (computing === "default") {
          get().updateNodesMetadata([{ nodeId, data: { computing: undefined } }]);
        } else {
          get().updateNodesMetadata([{ nodeId, data: { computing } }]);
        }
      } else if (Array.isArray(activity?.process.executors)) {
        const defaultComputing = get().workflowEvent.defaultComputing;
        const appConfig = await getApplicationConfig();
        let activityComputing = null;

        // from defaultComputing, take the first one in the executors list
        for (const exec of activity.process.executors) {
          switch (exec) {
            case SupportedExecutorsEnum.Local: {
              if (defaultComputing === exec) {
                activityComputing = exec;
              }
              break;
            }
            case SupportedExecutorsEnum.Cluster: {
              if (defaultComputing === exec && appConfig?.hpcEnabled) {
                activityComputing = exec;
              }
              break;
            }
            case SupportedExecutorsEnum.Aws: {
              if (defaultComputing === exec && appConfig?.cloudEnabled) {
                activityComputing = exec;
              }
              break;
            }
          }

          if (activityComputing != undefined) {
            break;
          }
        }

        get().updateNodesMetadata([
          { nodeId, data: { computing: activityComputing ?? (force ? undefined : node.data.computing) } },
        ]);
      }
    },
    setActivityDisabled(nodeId: string, disabled: boolean): void {
      const node = get().getNode(nodeId);
      if (!node) return;

      const nodesToDisable = disabled
        ? getDownstreamNodes(
            [nodeId],
            get().edges.map((edge) => ({ source: edge.source, target: edge.target })),
          )
        : [
            ...getUpstreamNodes(
              nodeId,
              get().edges.map((edge) => ({ source: edge.source, target: edge.target })),
            ),
          ];

      for (const nodeToDisable of nodesToDisable) {
        get().updateNodesMetadata([{ nodeId: nodeToDisable, data: { disabled: disabled } }]);
      }

      get().pushTimelineData({
        nodes: get().nodes,
        edges: get().edges,
        parameters: [],
      });
    },
    async forceAllActivitiesDefaultComputing() {
      const nodes = get().nodes;
      const defaultComputing = get().workflowEvent.defaultComputing;

      for (const node of nodes) {
        await get().setActivityComputing(node.id, undefined, defaultComputing == undefined);
      }
    },
    setSampleType(sampleTypeId) {
      set({ sampleTypeId });

      // Reset all TrackerParam.
      const trackerParameters = get()
        .getParameters()
        .filter(
          (parameter) =>
            // for both, Tracker Input and Output
            parameter.typeName === "SampleParam" || parameter.typeName === "TrackerParam",
        );

      for (const parameter of trackerParameters) {
        get().setActivityParameterValue(parameter.nodeId, parameter.parameterId, Nothing);
      }

      // Reset all TrackerParam in the CopyPaste Slice too
      const copyPasteData = get().copyPasteSessionData;

      if (copyPasteData) {
        const nodeIds = copyPasteData.nodes.map((n) => n.id);

        const trackerCopyPasteParams: Record<string, WorkflowParameter[]> = {};

        nodeIds.forEach((nodeId) => {
          trackerCopyPasteParams[nodeId] = copyPasteData.parameters[nodeId].map((param) => {
            if (param.typeName === "TrackerParam" || param.typeName === "SampleParam") {
              return {
                ...param,
                values: Nothing,
              };
            }
            return param;
          });
        });

        set({
          copyPasteSessionData: {
            ...copyPasteData,
            parameters: trackerCopyPasteParams,
          },
        });
      }
    },
    /** Reset the whole store to default */
    wipeWorkflowStore() {
      set({
        ...initialState,
        ...initialActivitySliceState,
        ...initialErrorsSliceState,
        ...initialExecutionSliceState,
        ...initialLoadSaveSliceState,
        ...initialReactFlowSliceState,
        ...initialCopyPasteSliceState,
        ...initialUndoRedoSliceState,
      });
    },
  };
};

export const createWorkflowEditorStore = () =>
  createStore<WorkflowEditorStore, [["zustand/subscribeWithSelector", never]]>(subscribeWithSelector(storeCreator));
export const WorkflowEditorContext = createContext<StoreApi<WorkflowEditorStore>>({} as StoreApi<WorkflowEditorStore>);

export function useWorkflowEditorStore<T>(
  selector: (state: WorkflowEditorStore) => T,
  equals?: (a: T, b: T) => boolean,
): T {
  const store = useContext(WorkflowEditorContext);

  if (!store) {
    throw new Error("Missing Store Provider");
  }

  return useStoreWithEqualityFn(store, selector, equals);
}
